import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
} from '@angular/core';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { InvoiceApi } from '@app/services/invoice-api';
import { InvoiceInterface } from '@app/interface/invoice';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ChangeDetectorRef } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { RecursiveInterface } from '@app/services/base-authenticated.service';
import { FormsModule } from '@angular/forms';
import { CorrispettiviReceiptsApi } from '@app/services/corrispettivi-receipts-api.service';
import { CorrispettiviReceiptsInterface } from '@app/interface/corrispettivi-receipts';
import { forkJoin, lastValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'izy-line-plot',
  imports: [
    CommonModule,
    MatCardModule,
    BaseChartDirective,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatFormFieldModule,
    FormsModule,
  ],
  templateUrl: './line-plot.component.html',
  styleUrl: './line-plot.component.scss',
})
export class LinePlotComponent implements OnInit, OnChanges {
  @Input() selectedYear: number = new Date().getFullYear();

  viewType: 'monthly' | 'daily' = 'monthly';
  selectedMonth: number = new Date().getMonth();

  months = [
    { value: 0, label: 'Gennaio' },
    { value: 1, label: 'Febbraio' },
    { value: 2, label: 'Marzo' },
    { value: 3, label: 'Aprile' },
    { value: 4, label: 'Maggio' },
    { value: 5, label: 'Giugno' },
    { value: 6, label: 'Luglio' },
    { value: 7, label: 'Agosto' },
    { value: 8, label: 'Settembre' },
    { value: 9, label: 'Ottobre' },
    { value: 10, label: 'Novembre' },
    { value: 11, label: 'Dicembre' },
  ];

  public lineChartData: ChartData<'line'> = {
    labels: [],
    datasets: [],
  };

  public lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#616161',
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.1)' },
        ticks: {
          color: '#616161',
          callback: (value) => '€' + value.toLocaleString('it-IT'),
          // Fewer ticks on mobile
          maxTicksLimit: this.isMobile() ? 5 : 8,
        },
        title: {
          display: !this.isMobile(), // Hide on mobile
          text: 'Ammontare (€)',
          color: '#616161',
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#616161',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        padding: 12,
        cornerRadius: 12,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const value = context.raw as number;
            const formattedValue = value.toLocaleString('it-IT', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
            return `Fatturato: €${formattedValue}`;
          },
          afterLabel: (context) => {
            const count = this.documentCounts[context.dataIndex] ?? 0;
            return `Documenti emessi: ${count}`;
          },
        },
      },
    },
    elements: {
      line: {
        tension: 0.4,
        borderWidth: 3,
      },
      point: {
        radius: 4,
        hoverRadius: 8,
        backgroundColor: 'white',
        borderWidth: 2,
      },
    },
  };

  public lineChartType: ChartType = 'line';
  public isLoading = true;
  public errorMessage: string | null = null;
  public documentCounts: number[] = [];

  constructor(
    private invoiceApi: InvoiceApi,
    private Corrispettiviservice: CorrispettiviReceiptsApi,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.loadInvoiceData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedYear'] && !changes['selectedYear'].firstChange) {
      this.loadInvoiceData();
    }

    this.cdr.detectChanges();
  }

  onViewTypeChange(): void {
    this.loadInvoiceData();
  }

  getCssVariable(name: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
  }

  getCssVariableWithOpacity(name: string, opacity: number = 1): string {
    const color = this.getCssVariable(name);
    return this.hexToRgba(color, opacity);
  }

  isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  private hexToRgba(hex: string, opacity: number): string {
    // Remove # if present
    hex = hex.replace('#', '');

    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // now actually invoices and corrispettivi
  loadInvoiceData(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.isLoading = true;
    this.errorMessage = null;
    this.cdr.detectChanges();

    const baseParams: any = {
      is_draft: false,
      is_preventive: false,
      is_deleted: false,
      is_saved_for_preview: false,
    };

    let invoiceParams: any;
    let corrispettiviParams: any;

    if (this.viewType === 'monthly') {
      invoiceParams = {
        ...baseParams,
        emission_date__gte: `${this.selectedYear}-01-01`,
        emission_date__lte: `${this.selectedYear}-12-31`,
      };
      corrispettiviParams = {
        ...baseParams,
        transmission_date__gte: `${this.selectedYear}-01-01`,
        transmission_date__lte: `${this.selectedYear}-12-31`,
      };
    } else {
      const month = this.selectedMonth + 1;
      const daysInMonth = new Date(
        this.selectedYear,
        this.selectedMonth + 1,
        0,
      ).getDate();
      const dateFrom = `${this.selectedYear}-${month.toString().padStart(2, '0')}-01`;
      const dateTo = `${this.selectedYear}-${month.toString().padStart(2, '0')}-${daysInMonth}`;
      invoiceParams = {
        ...baseParams,
        emission_date__gte: dateFrom,
        emission_date__lte: dateTo,
      };
      corrispettiviParams = {
        ...baseParams,
        transmission_date__gte: dateFrom,
        transmission_date__lte: dateTo,
      };
    }

    // 1. Prepare the Observables
    // filter(r => r.completed) + take(1) ensures we wait for ALL pages to be loaded
    // before processing. Using take(1) alone would only capture the first page.
    const invoices$ = this.invoiceApi
      .getAllInvoices<InvoiceInterface>(organization_id, invoiceParams)
      .pipe(
        filter((r) => r.completed),
        take(1),
      );

    const corrispettivi$ =
      this.Corrispettiviservice.getAllCorrispettiviReceipts(
        organization_id,
        corrispettiviParams,
      ).pipe(
        filter((r) => r.completed),
        take(1),
      );

    // 2. Wait for both to finish
    forkJoin({
      invoices: invoices$,
      corrispettivi: corrispettivi$,
    }).subscribe({
      next: (result) => {
        const invoicesList = result.invoices.results || [];
        const corrispettiviList = result.corrispettivi.results || [];

        // 3. Select processing logic based on view type
        if (this.viewType === 'monthly') {
          this.processMonthlyData(invoicesList, corrispettiviList);
        } else {
          this.processDailyData(invoicesList, corrispettiviList);
        }

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load chart data:', err);
        this.errorMessage = 'Errore nel caricamento dei dati.';
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private processMonthlyData(
    invoices: InvoiceInterface[],
    corrispettivi: CorrispettiviReceiptsInterface[],
  ): void {
    if (
      (!invoices && !corrispettivi) ||
      invoices.length + corrispettivi.length === 0
    ) {
      this.errorMessage = "Nessun documento per l'anno selezionato.";
      return;
    }

    // Initialize monthly data
    const monthlyTotals = new Array(12).fill(0);
    const monthlyCounts = new Array(12).fill(0);
    const monthLabels = [
      'Gen',
      'Feb',
      'Mar',
      'Apr',
      'Mag',
      'Giu',
      'Lug',
      'Ago',
      'Set',
      'Ott',
      'Nov',
      'Dic',
    ];

    invoices.forEach((invoice) => {
      const invoiceDate = new Date(invoice.emission_date);
      const month = invoiceDate.getMonth();

      if (invoice.document_type === 'TD04') {
        monthlyTotals[month] -= Number(invoice.amount) || 0; // subtract if Nota di credito
      } else {
        monthlyTotals[month] += Number(invoice.amount) || 0;
      }
      monthlyCounts[month]++;
    });
    corrispettivi.forEach((corrispettivo) => {
      const corrDate = new Date(corrispettivo.transmission_date);
      const month = corrDate.getMonth();
      monthlyTotals[month] += Number(corrispettivo.amount) || 0;
      monthlyCounts[month]++;
    });

    this.documentCounts = monthlyCounts;

    this.lineChartData = {
      labels: monthLabels,
      datasets: [
        {
          label: 'Fatturato Mensile',
          data: monthlyTotals,
          borderColor: this.getCssVariable('--info-color'),
          backgroundColor: this.hexToRgba(
            this.getCssVariable('--info-2-color'),
            0.5,
          ),
          pointBorderColor: this.getCssVariable('--info-color'),
          pointBackgroundColor: 'white',
          pointHoverBackgroundColor: this.getCssVariable('--info-color'),
          pointHoverBorderColor: 'white',
          fill: true,
        },
      ],
    };

    this.cdr.detectChanges();
  }

  private processDailyData(
    invoices: InvoiceInterface[],
    corrispettivi: CorrispettiviReceiptsInterface[],
  ): void {
    if (
      (!invoices && !corrispettivi) ||
      invoices.length + corrispettivi.length === 0
    ) {
      this.errorMessage = "Nessun documento per l'anno selezionato.";
      return;
    }

    const daysInMonth = new Date(
      this.selectedYear,
      this.selectedMonth + 1,
      0,
    ).getDate();
    const dailyTotals = new Array(daysInMonth).fill(0);
    const dailyCounts = new Array(daysInMonth).fill(0);
    const dayLabels = Array.from({ length: daysInMonth }, (_, i) =>
      (i + 1).toString(),
    );

    invoices.forEach((invoice) => {
      const invoiceDate = new Date(invoice.emission_date);
      const day = invoiceDate.getDate() - 1;

      if (invoice.document_type === 'TD04') {
        dailyTotals[day] -= Number(invoice.amount) || 0; // subtract if Nota di credito
      } else {
        dailyTotals[day] += Number(invoice.amount) || 0;
      }
      dailyCounts[day]++;
    });
    corrispettivi.forEach((corrispettivo) => {
      const corrDate = new Date(corrispettivo.transmission_date);
      const day = corrDate.getDate() - 1;
      dailyTotals[day] += Number(corrispettivo.amount) || 0;
      dailyCounts[day]++;
    });

    this.documentCounts = dailyCounts;

    this.lineChartData = {
      labels: dayLabels,
      datasets: [
        {
          label: `Fatturato Giornaliero - ${this.months[this.selectedMonth].label} ${this.selectedYear}`,
          data: dailyTotals,
          borderColor: this.getCssVariable('--warn-color'),
          backgroundColor: this.hexToRgba(
            this.getCssVariable('--warn-color'),
            0.5,
          ),
          pointBorderColor: this.getCssVariable('--warn-color'),
          pointBackgroundColor: 'white',
          pointHoverBackgroundColor: this.getCssVariable('--warn-color'),
          pointHoverBorderColor: 'white',
          fill: true,
        },
      ],
    };
  }

  refreshData(): void {
    this.loadInvoiceData();
    this.cdr.detectChanges();
  }
}
